import { type FormEvent, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, CheckCircle2, Lock, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/Input';
import { registrarMedico } from '../services/authService';
import styles from './RegistroPage.module.css';

function passwordStrength(pw: string): { level: number; label: string } {
  let score = 0;
  if (pw.length >= 12) score++;
  if (pw.length >= 16) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pw)) score++;

  if (score <= 2) return { level: 1, label: 'Debil' };
  if (score <= 4) return { level: 2, label: 'Media' };
  return { level: 3, label: 'Fuerte' };
}

const REQUIREMENTS = [
  { test: (pw: string) => pw.length >= 8, label: 'Minimo 8 caracteres' },
  { test: (pw: string) => /[A-Z]/.test(pw), label: 'Una letra mayuscula' },
  { test: (pw: string) => /[a-z]/.test(pw), label: 'Una letra minuscula' },
  { test: (pw: string) => /\d/.test(pw), label: 'Un numero' },
  { test: (pw: string) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pw), label: 'Un caracter especial' },
];

export default function RegistroPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!authLoading && user) navigate('/salas', { replace: true });
  }, [user, authLoading, navigate]);

  const strength = passwordStrength(password);
  const passwordsMatch = password === confirmar && confirmar.length > 0;
  const allRequirementsMet = REQUIREMENTS.every((r) => r.test(password));
  const canSubmit = email.trim() !== '' && allRequirementsMet && passwordsMatch && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');

    try {
      await registrarMedico(email, password, confirmar);
      setSuccess(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al crear la cuenta';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) return null;

  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.centerPanel}>
          <div className={styles.formWrapper}>
            <div className={styles.brand}>
              <img className={styles.brandLogo} src="/minsalud-logo.png" alt="MINSAlud — Portal del Paciente" />
            </div>
            <div className={styles.successCard}>
              <CheckCircle2 size={48} className={styles.successIcon} />
              <h2 className={styles.successTitle}>Cuenta creada exitosamente</h2>
              <p className={styles.successText}>
                Hemos enviado un enlace de verificacion a <strong>{email}</strong>.
                Revise su bandeja de entrada y haga clic en el enlace para activar su cuenta.
              </p>
              <Link to="/" className={styles.successLink}>Ir al inicio de sesion</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.centerPanel}>
        <div className={styles.formWrapper}>
          <div className={styles.brand}>
            <img className={styles.brandLogo} src="/minsalud-logo.png" alt="MINSAlud — Portal del Paciente" />
          </div>

          <div className={styles.eyebrow}>
            <span className={styles.eyebrowDot} />
            <span>Solicitud de acceso · Portal de Teleconsulta</span>
          </div>

          <h1 className={styles.title}>
            Cree su <em>cuenta</em>.
          </h1>
          <p className={styles.subtitle}>
            Ingrese su correo institucional para registrarse.
            Debe estar pre-autorizado por un administrador.
          </p>

          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              label="Correo institucional"
              type="email"
              placeholder="correo@ejemplo.com"
              leftIcon={<Mail size={16} />}
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(''); }}
              disabled={submitting}
              autoComplete="email"
              required
            />

            <Input
              label="Contrasena"
              type="password"
              placeholder="Minimo 8 caracteres"
              leftIcon={<Lock size={16} />}
              showPasswordToggle
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              disabled={submitting}
              autoComplete="new-password"
              required
            />

            {/* Strength indicator */}
            {password.length > 0 && (
              <div className={styles.strengthBar}>
                <div
                  className={styles.strengthFill}
                  data-level={strength.level}
                  style={{ width: `${(strength.level / 3) * 100}%` }}
                />
                <span className={styles.strengthLabel}>{strength.label}</span>
              </div>
            )}

            {/* Requirements checklist */}
            {password.length > 0 && (
              <ul className={styles.requirements}>
                {REQUIREMENTS.map((r) => (
                  <li key={r.label} className={r.test(password) ? styles.reqMet : styles.reqUnmet}>
                    {r.test(password) ? <CheckCircle2 size={14} /> : <span className={styles.reqDot} />}
                    {r.label}
                  </li>
                ))}
              </ul>
            )}

            <Input
              label="Confirmar contrasena"
              type="password"
              placeholder="Repita su contrasena"
              leftIcon={<Lock size={16} />}
              showPasswordToggle
              value={confirmar}
              onChange={(e) => { setConfirmar(e.target.value); setError(''); }}
              error={confirmar.length > 0 && !passwordsMatch ? 'Las contrasenas no coinciden' : undefined}
              disabled={submitting}
              autoComplete="new-password"
              required
            />

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={!canSubmit}
            >
              {submitting ? 'Creando cuenta...' : (
                <>
                  Crear cuenta
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            {error && (
              <div className={styles.errorAlert} role="alert">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}
          </form>

          <div className={styles.footNote}>
            ¿Ya tiene cuenta?{' '}
            <Link to="/" className={styles.footLink}>Iniciar sesion</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
